import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { AdminCourseNotFound } from "@/features/admin/components/AdminCourseNotFound";
import { PreviewVersionPage } from "@/features/admin/edit-course/PreviewVersionPage";
import { adminGetCourse } from "@/services/admin/courses";
import type { CourseDetailDto } from "@/features/admin/types";
import { CourseVersionStatus } from "@/features/admin/types";

export const Route = createFileRoute("/admin/courses/$courseId_/versions/$versionId/preview")({
  component: PreviewVersionRoute,
  validateSearch: (search: Record<string, unknown>): { moduleId?: string } => ({
    moduleId: typeof search.moduleId === "string" ? search.moduleId : undefined,
  }),
  remountDeps: ({ params }) => `${params.courseId}/${params.versionId}`,
});

/** Preview a version as a learner. An archived or missing version goes back to Content and versions. */
function PreviewVersionRoute() {
  const { courseId, versionId } = Route.useParams();
  const { moduleId } = Route.useSearch();

  // The same query as the edit course page, so going back and forth shares one copy of the course.
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "courses", courseId],
    queryFn: ({ signal }) => adminGetCourse(courseId, signal),
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  }) as { data: CourseDetailDto | undefined; isLoading: boolean; error: Error | null };

  if (error && !data) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return (
        <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
          <AdminCourseNotFound />
        </ProtectedRoute>
      );
    }
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-sm text-red-600">Unable to load course details.</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  if (isLoading || !data) {
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-tertiary text-sm">Loading course details…</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  const version = data.versions?.find((item) => item.id === versionId);
  // The server refuses to preview an archived version.
  if (!version?.id || version.status === CourseVersionStatus.Archived) {
    return (
      <Navigate
        to="/admin/courses/$courseId"
        params={{ courseId }}
        search={{ tab: "content" }}
        replace
      />
    );
  }

  return (
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <PreviewVersionPage
          courseId={courseId}
          course={data}
          version={{ ...version, id: version.id }}
          moduleId={moduleId}
        />
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
